/**
 * Discount helpers for quiz-results flow.
 * This file mirrors core Angular DiscountService behavior in framework-free form.
 */
const EXTERNAL_WORKERS_API_URL = "https://external-workers-api.herokuapp.com/api/";
const EXTERNAL_WORKERS_AUTH_TOKEN =
  "eyJhbGciOiJIUzI1NiJ9.eyJhcHBfaWQiOjF9.CAmOEIU8dvFR07ZHdUEvjms1S6ir9J1GEBlXatxeyss";

/**
 * @param {string} name
 * @returns {string | undefined}
 */
export function getCookie(name) {
  const match = document.cookie.match(`(^|; )${name}=([^;]*)`);
  return match ? match[2] : undefined;
}

/**
 * @param {string} name
 * @param {string} value
 * @param {number} days
 */
export function setCookie(name, value, days = 0) {
  let expires = "";
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = `; expires=${date.toUTCString()}`;
  }
  document.cookie = `${name}=${value || ""}${expires}; path=/; domain=geologie.com; SameSite=Lax`;
}

/**
 * @param {string} key
 * @returns {string | null}
 */
export function getQueryParam(key) {
  const params = new URLSearchParams(window.location.search);
  return params.get(key);
}

/**
 * @param {any} discountData
 * @returns {{ code: string, value: number }}
 */
export function toDiscountProperty(discountData) {
  return {
    code: discountData?.discount?.code || "",
    value: Math.abs(Number(discountData?.price_rule?.value || 0)),
  };
}

/**
 * @param {any} discountJson
 * @param {any} regimenProduct
 * @returns {boolean}
 */
export function isDiscountEligible(discountJson, regimenProduct) {
  if (!discountJson || !regimenProduct) return false;

  const entitledCollectionIds = discountJson?.price_rule?.entitled_collection_ids || [];
  const entitledProductIds = discountJson?.price_rule?.entitled_product_ids || [];

  const collectionMatch = (regimenProduct.collections || []).some((/** @type {any} */ collection) =>
    entitledCollectionIds.includes(collection.id),
  );
  const productMatch = entitledProductIds.includes(regimenProduct.id);

  return collectionMatch || productMatch;
}

/**
 * Resolve discount source in the same priority order as Angular:
 * query param -> cookie -> default discount.
 *
 * @param {Object} options
 * @param {any} options.regimenProduct
 * @param {string=} options.discountEndpoint
 * @returns {Promise<{ discount: any, discountWithRefills: any, source: string }>}
 */
export async function resolveDiscountState({ regimenProduct, discountEndpoint }) {
  const defaultDiscount =
    /** @type {any} */ (window)?.geologie_purchase_flow?.diagnostic?.skincare?.discount || null;

  const queryCode = getQueryParam("discount");
  const cookieCode = getCookie("g-trial-discount");
  const requestedCode = queryCode || cookieCode;
  if (!requestedCode) {
    return {
      discount: defaultDiscount,
      discountWithRefills: defaultDiscount,
      source: "default",
    };
  }
  const cachedRaw = getCookie("g_trial_discount_data");
  const cached = safeJsonParse(cachedRaw, null);
  const isSameCachedCode =
    cached?.discount?.code?.toLowerCase?.() === requestedCode.toLowerCase();

  if (isSameCachedCode && isDiscountEligible(cached, regimenProduct)) {
    const discount = toDiscountProperty(cached);
    return {
      discount,
      discountWithRefills: discount,
      source: "cookie",
    };
  }

  const discountData = await fetchDiscountDetails(requestedCode, discountEndpoint);
  if (discountData && isDiscountEligible(discountData, regimenProduct)) {
    setCookie("g_trial_discount_data", JSON.stringify(discountData));
    setCookie("g-trial-discount", requestedCode);
    const discount = toDiscountProperty(discountData);
    return {
      discount,
      discountWithRefills: discount,
      source: "api",
    };
  }

  return {
    discount: defaultDiscount,
    discountWithRefills: defaultDiscount,
    source: "default_fallback",
  };
}

/**
 * Global entrypoint-safe helper:
 * - if URL has ?discount=CODE, fetch discount payload and persist it in cookies.
 *
 * @returns {Promise<void>}
 */
export async function syncDiscountFromQueryToCookie() {
  const queryCode = (getQueryParam("discount") || "").trim();
  if (!queryCode) return;

  const cachedRaw = getCookie("g_trial_discount_data");
  const cached = safeJsonParse(cachedRaw, null);
  const cachedCode = String(cached?.discount?.code || "").toLowerCase();
  if (cachedCode === queryCode.toLowerCase()) return;

  const discountData = await fetchDiscountDetails(queryCode);
  if (!discountData) return;

  setCookie("g_trial_discount_data", JSON.stringify(discountData));
  setCookie("g-trial-discount", queryCode);
}

/**
 * @param {string} code
 * @param {string | undefined} endpoint
 * @returns {Promise<any | null>}
 */
async function fetchDiscountDetails(code, endpoint = undefined) {
  // Keep function signature compatible, but use internal default endpoint by design.
  const _ignoredEndpoint = endpoint;
  void _ignoredEndpoint;

  const email = getDiscountRequestEmail();
  let path = `shopify/discounts/${encodeURIComponent(code)}`;
  if (email) {
    path += `?email=${encodeURIComponent(email)}`;
  }

  try {
    const response = await fetch(`${EXTERNAL_WORKERS_API_URL}${path}`, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Referrer-Policy": "origin",
        Authentication: EXTERNAL_WORKERS_AUTH_TOKEN,
      },
      credentials: "same-origin",
    });
    if (!response.ok) return null;
    return await response.json();
  } catch (_error) {
    return null;
  }
}

/**
 * @param {string | undefined} value
 * @param {any} fallback
 * @returns {any}
 */
function safeJsonParse(value, fallback) {
  try {
    if (!value) return fallback;
    return JSON.parse(value);
  } catch (_error) {
    return fallback;
  }
}

/**
 * Mirrors CredentialsService.email() behavior from backend-lib:
 * 1) window.obscure_customer.email
 * 2) window.shopify_customer.email
 * 3) cookie g_results_email
 *
 * @returns {string}
 */
function getDiscountRequestEmail() {
  const fromObscure = /** @type {any} */ (window)?.obscure_customer?.email;
  const fromShopify = /** @type {any} */ (window)?.shopify_customer?.email;
  const fromCookie = getCookie("g_results_email");
  return fromObscure || fromShopify || fromCookie || "";
}

/**
 * OTP behavior:
 * - OTP ON => active discount is null
 * - OTP OFF => restore refill discount
 *
 * @param {boolean} isOtpOn
 * @param {any} discountWithRefills
 * @returns {any}
 */
export function getDiscountForOtpState(isOtpOn, discountWithRefills) {
  return isOtpOn ? null : discountWithRefills;
}
